import * as React from 'react'
import { invariant } from '@epic-web/invariant'
import { getId } from '@basehub/utils/id/index'
import { ClipboardCopyIcon } from '@radix-ui/react-icons'
import { toast } from 'sonner'
import { isHotkey } from 'is-hotkey'
import {
  type Block,
  type BlockWithChildren,
  isCommentBlock,
  isInstanceBlock,
  isVariantSetBlock,
} from '@basehub/data/replicache/index'
import { useEditorStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/editor-store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { tabContentId } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/tab-content/wrapper'
import { useTabsStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'

import {
  getEditorElement,
  isScrolledIntoView,
  getDomHasSelectedText,
  getShouldPreventCopyBlock,
} from '~/lib/editor'
import {
  useIsGuestInHisBranch,
  useIsMemberOrHigher,
} from '~/hooks-data/use-user-role'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import {
  idToSourceBlockId,
  sourceBlockIdToId,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  copyBlockToClipboard,
  pasteBlocksFromClipboard,
} from '~/hooks-data/copy-paste'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { isCaretOnLastLine } from '~/sotano-kit/primitives/textarea/is-caret-on-last-line'
import {
  commentExtensionsForTransformations,
  jsonToPlainText,
  stateToJson,
} from '@basehub/tiptap-api-utils'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { DeleteBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { useEditorMode } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/editor-mode-provider'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'
import { useSelectedBranch } from '~/lib/commits/use-selected-branch-or-commit'

export const useEditorKeyboardNavigation = ({
  block,
  path,
  disabled,
}: {
  block: BlockWithChildren | undefined
  path: string | undefined
  disabled?: boolean
}) => {
  const blockTreeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  const { data } = useRepoReplicache()
  const isMemberOrHigher = useIsMemberOrHigher()
  const editorMode = useEditorMode()
  const editModeIsEnabled = useUserPreferences((s) => s.editMode)
  const replicache = data?.replicache
  const addTab = useTabsStore((s) => s.addTab)
  const treeRef = useWorkingTreeStore((s) => s.treeRef)

  const setFocus = useRepoStore((s) => s.setFocus)
  const focusType = useRepoStore((s) => s.focus?.type)
  const cutBlocks = useRepoStore((s) => s.cutBlocks)
  const setCutBlocks = useRepoStore((s) => s.setCutBlocks)
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const branch = useSelectedBranch().branch
  const selectedBlockId = useEditorStore((s) => s.selectedBlockId)
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const playgroundCookie = usePlaygroundCookie()
  const isPlayground =
    branch &&
    (!!branch.playgroundId || (!!playgroundCookie && branch.isDefault))

  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const canAlterBlockSchema = React.useCallback(
    ({
      b,
      parent,
    }: {
      b: Block
      parent: Block | null
    }): {
      value: boolean
      reason?: string
    } => {
      if (b.id === block?.id) return { value: false }
      if (parent?.type === 'instance') {
        return { value: false, reason: `Can't edit instance blocks' schema` }
      }
      if (
        parent?.type === 'reference' ||
        parent?.type === 'rich-text' ||
        parent?.type === 'list'
      ) {
        // doesn't alter schema
        return { value: true }
      }
      if (isMemberOrHigher || isGuestInHisBranch || isPlayground) {
        if (editModeIsEnabled) {
          return {
            value: false,
            reason: `Disable Edit Mode to edit blocks' schema`,
          }
        }
      } else {
        return {
          value: false,
          reason: `You do not have permission to edit blocks' schema`,
        }
      }
      if (isMemberOrHigher && !editModeIsEnabled) return { value: true }

      return { value: true }
    },
    [
      block?.id,
      editModeIsEnabled,
      isGuestInHisBranch,
      isMemberOrHigher,
      isPlayground,
    ]
  )

  // for cutting blocks
  const canAlterThisBlocksSchema = React.useMemo(() => {
    if (!block || !blockTreeManagerRef.current) return false
    const selectedNode = blockTreeManagerRef.current.getNode(block.id)
    if (!selectedNode) return false

    const access = canAlterBlockSchema({
      b: selectedNode.value,
      parent: selectedNode.parentKey
        ? (blockTreeManagerRef.current.getNode(selectedNode.parentKey)?.value ??
          null)
        : null,
    })

    return access.value
  }, [block, blockTreeManagerRef, canAlterBlockSchema])

  const onFocusPrevListItem = React.useCallback(
    (e: KeyboardEvent, options?: { bypassFocusValueCheck: boolean }) => {
      if (!treeRef.current) return
      const hasSelectedText = getDomHasSelectedText()
      if (hasSelectedText || !block) return

      const { editor, focusIsInsideEditor, focusedElement } = getEditorElement()
      if (!focusIsInsideEditor) return

      const shouldPrevent = getShouldPreventCopyBlock(focusedElement, focusType)

      const shouldPreventNavigation =
        (focusedElement &&
          (['TEXTAREA', 'INPUT'].includes(focusedElement.nodeName) ||
            focusedElement.role === 'option' ||
            focusedElement.role === 'menuitem')) ||
        shouldPrevent

      if (shouldPreventNavigation && !options?.bypassFocusValueCheck) {
        return
      }

      const allBlocks = Array.from(
        editor.querySelectorAll<HTMLElement>('div[data-block-id]')
      )
      const blocks = allBlocks.reduce<HTMLElement[]>((acum, current) => {
        const arrayOfIds = acum.map((el) => el.dataset.blockId)

        if (arrayOfIds.includes(current.dataset.blockId)) {
          return acum
        }

        // skip comments if the block is an instance, since they are read only and can not be focused
        if (
          block &&
          isInstanceBlock(block) &&
          Boolean(
            current.dataset.commentBlock ||
              current.querySelector('div[data-comment-block]')
          )
        ) {
          return acum
        }
        acum.push(current)

        return acum
      }, [])
      const focusedBlockIndex = blocks.findIndex(
        (el) => el.dataset.blockId === selectedBlockId
      )

      e.preventDefault()

      if (focusedElement instanceof HTMLButtonElement) {
        focusedElement.blur()
      }

      let blockIndexToFocus = 0

      if (
        treeRef.current &&
        selectedBlockId &&
        isVariantSetBlock(
          treeRef.current.getNode(selectedBlockId, { required: true }).value
        )
      ) {
        // is on a variant set so we'll try to focus the previous variant
        const variantsInSetBlock = blocks[focusedBlockIndex]?.querySelectorAll(
          'button[data-variant-item]'
        ) as NodeListOf<HTMLButtonElement> | undefined
        if (variantsInSetBlock && variantsInSetBlock.length > 0) {
          const foundIndex = Array.from(variantsInSetBlock).findIndex(
            (v) => v.dataset.active === 'true'
          )
          if (foundIndex - 1 >= 0) {
            variantsInSetBlock[foundIndex - 1]?.click()
            return
          }
        }
      }

      // is focusing doc title, lets focus the first one
      if (isHotkey('Meta+ArrowUp', e)) {
        blockIndexToFocus = 0
      } else if (focusedBlockIndex === -1) blockIndexToFocus = 0
      else blockIndexToFocus = focusedBlockIndex - 1

      const prevBlock = blocks[blockIndexToFocus]
      if (!prevBlock || !prevBlock.dataset.blockId) {
        if (!block) return

        setFocus({
          type: 'block:document-title',
          blockId: idToSourceBlockId(block),
        })
        return
      }

      setSelectedBlockId(
        idToSourceBlockId(prevBlock.dataset.blockId, treeRef.current)
      )

      const prevBlockWrapper = prevBlock.closest<HTMLDivElement>(
        `#${tabContentId} > div`
      ) as HTMLDivElement

      if (!prevBlockWrapper || isScrolledIntoView(prevBlockWrapper)) {
        return
      }

      prevBlockWrapper.scrollIntoView({ block: 'nearest' })
    },
    [treeRef, block, focusType, setSelectedBlockId, selectedBlockId, setFocus]
  )

  const onFocusNextListItem = React.useCallback(
    (e: KeyboardEvent, options?: { bypassFocusValueCheck: boolean }) => {
      if (!treeRef.current) return
      const hasSelectedText = getDomHasSelectedText()
      if (hasSelectedText || !block) return

      const { editor, focusIsInsideEditor, focusedElement } = getEditorElement()
      if (!focusIsInsideEditor) return

      const shouldPrevent = getShouldPreventCopyBlock(focusedElement, focusType)
      let shouldPreventNavigation =
        (focusedElement instanceof HTMLTextAreaElement &&
          focusedElement.role !== 'document-title') ||
        focusedElement instanceof HTMLInputElement ||
        focusedElement?.getAttribute('contenteditable') ||
        focusedElement?.role === 'option' ||
        focusedElement?.role === 'menuitem' ||
        shouldPrevent

      if (
        e.key === 'ArrowDown' &&
        focusedElement instanceof HTMLTextAreaElement &&
        focusedElement.dataset.documentTitle === idToSourceBlockId(block)
      ) {
        if (!isCaretOnLastLine(focusedElement)) {
          // then the caret is not in the last line, so we prevent the navigation
          shouldPreventNavigation = true
        }
      }

      if (shouldPreventNavigation && !options?.bypassFocusValueCheck) return

      const allBlocks = Array.from(
        editor.querySelectorAll<HTMLElement>('div[data-block-id]')
      )

      const blocks = allBlocks.reduce<HTMLElement[]>((acum, current) => {
        const arrayOfIds = acum.map((el) => el.dataset.blockId)

        if (arrayOfIds.includes(current.dataset.blockId)) return acum

        // skip comments if the block is an instance, since they are read only and can not be focused
        if (
          block &&
          isInstanceBlock(block) &&
          Boolean(
            current.dataset.commentBlock ||
              current.querySelector('div[data-comment-block]')
          )
        ) {
          return acum
        }

        acum.push(current)

        return acum
      }, [])

      const focusedBlockIndex = blocks.findIndex(
        (el) => el.dataset.blockId === selectedBlockId
      )

      e.preventDefault()

      let blockIndexToFocus = 0
      const firstBlockIndex = 0
      const lastBlockIndex = blocks.length - 1

      if (focusedElement instanceof HTMLButtonElement) {
        focusedElement.blur()
      }

      // is focusing doc title, lets focus the first one
      if (
        focusedElement instanceof HTMLTextAreaElement &&
        focusedElement.role === 'document-title'
      ) {
        focusedElement.blur()
        if (isHotkey('Meta+ArrowDown', e)) {
          blockIndexToFocus = lastBlockIndex
        } else {
          blockIndexToFocus = firstBlockIndex
        }
      } else if (isHotkey('Meta+ArrowDown', e)) {
        blockIndexToFocus = lastBlockIndex
      } else if (focusedBlockIndex === -1 && e.key === 'ArrowDown') {
        blockIndexToFocus = lastBlockIndex
      } else {
        if (
          treeRef.current &&
          selectedBlockId &&
          isVariantSetBlock(
            treeRef.current.getNode(selectedBlockId, { required: true }).value
          )
        ) {
          const variantsInSetBlock = blocks[
            focusedBlockIndex
          ]?.querySelectorAll('button[data-variant-item]') as
            | NodeListOf<HTMLButtonElement>
            | undefined

          if (variantsInSetBlock && variantsInSetBlock.length > 0) {
            const foundIndex = Array.from(variantsInSetBlock).findIndex(
              (v) => v.dataset.active === 'true'
            )
            if (foundIndex + 1 < variantsInSetBlock.length) {
              variantsInSetBlock[foundIndex + 1]?.click()
              return
            }
          }
        }
        // if not variant set or there's no more variants, jump to the next block
        blockIndexToFocus = focusedBlockIndex + 1
      }

      const nextBlock = blocks[blockIndexToFocus]
      if (!nextBlock || !nextBlock.dataset.blockId) {
        return
      }
      setSelectedBlockId(
        idToSourceBlockId(nextBlock.dataset.blockId, treeRef.current)
      )

      const nextBlockWrapper = nextBlock.closest<HTMLDivElement>(
        `#${tabContentId} > div`
      ) as HTMLDivElement

      if (!nextBlockWrapper || isScrolledIntoView(nextBlockWrapper)) {
        return
      }

      nextBlockWrapper.scrollIntoView({ block: 'nearest' })
    },
    [setSelectedBlockId, selectedBlockId, block, focusType, treeRef]
  )

  const onFocusBlockWrapper = React.useCallback(
    (e: KeyboardEvent) => {
      if (!treeRef.current) return
      const { focusIsInsideEditor, focusedElement } = getEditorElement()

      const hasSelectedText = getDomHasSelectedText()
      if (hasSelectedText || !focusIsInsideEditor) return

      let shouldPreventNavigation =
        focusedElement &&
        (focusedElement.role === 'option' || focusedElement.role === 'menuitem')

      if (focusedElement instanceof HTMLElement) {
        if (
          focusedElement.classList.contains('slash-command-open') ||
          focusedElement.closest('.slash-command-open')
        ) {
          shouldPreventNavigation = true
        }
      }

      if (shouldPreventNavigation) return

      // for comment blocks (prevent)
      if (
        focusedElement instanceof HTMLDivElement &&
        focusedElement?.getAttribute('contentEditable') &&
        focusedElement.parentElement?.dataset.commentBlock
      ) {
        return
      }

      if (selectedBlockId !== block?.id) {
        if (!focusIsInsideEditor || !(focusedElement instanceof HTMLElement)) {
          return
        }
        e.preventDefault()

        const shouldPrevent = getShouldPreventCopyBlock(
          focusedElement,
          focusType
        )

        const selectedWrapperElement = document.querySelector<HTMLElement>(
          `[data-block-id="${selectedBlockId}"]`
        )
        const parentWrapperElement = selectedWrapperElement
          ? (selectedWrapperElement.parentElement?.closest<HTMLElement>(
              '[data-block-id]'
            ) ?? null)
          : null
        const parentId = parentWrapperElement?.dataset.blockId

        if (shouldPrevent) {
          const blockIdToFocus =
            selectedBlockId ?? focusedElement.dataset.blockId
          if (!blockIdToFocus) return
          focusedElement.blur()
          setFocus({
            blockId: blockIdToFocus,
            type: 'block:wrapper',
          })
        } else if (parentId) {
          setFocus({
            blockId: parentId,
            type: 'block:wrapper',
          })
        }
      }
    },
    [treeRef, selectedBlockId, block, focusType, setFocus]
  )

  const onFocusBlockValue = React.useCallback(
    (e: KeyboardEvent) => {
      // is using the create new block underneath shortcut, so we prevent
      if (isHotkey('meta+enter', e)) return

      const { focusIsInsideEditor, focusedElement } = getEditorElement()
      if (!focusIsInsideEditor) return

      if (
        selectedBlockId &&
        !['INPUT', 'BUTTON', 'A', 'TEXTAREA'].includes(
          focusedElement?.tagName ?? ''
        ) &&
        !focusedElement?.getAttribute('contentEditable')
      ) {
        e.preventDefault()
        e.stopPropagation()
        setFocus({
          blockId: selectedBlockId,
          type: 'block:value',
        })
      }
    },
    [selectedBlockId, setFocus]
  )

  const onDeleteBlock = React.useCallback(
    async (e: KeyboardEvent) => {
      if (!replicache || !block || !selectedBlockId || !treeRef.current) {
        return
      }

      const hasSelectedText = getDomHasSelectedText()
      if (hasSelectedText) return

      const { focusIsInsideEditor, focusedElement } = getEditorElement()
      if (!focusIsInsideEditor) return
      if (!(focusedElement instanceof HTMLElement)) return

      const isCommentWithContent =
        focusedElement instanceof HTMLDivElement &&
        focusedElement?.getAttribute('contentEditable') &&
        focusedElement.parentElement?.dataset.commentBlock &&
        Boolean(focusedElement.innerText.trim())

      if (
        !selectedBlockId ||
        (block &&
          e.target instanceof HTMLTextAreaElement &&
          !isCommentBlock(block)) ||
        focusedElement instanceof HTMLTextAreaElement ||
        focusedElement instanceof HTMLInputElement ||
        (focusedElement instanceof HTMLDivElement &&
          focusedElement?.getAttribute('contentEditable') &&
          !focusedElement.parentElement?.dataset.commentBlock) ||
        isCommentWithContent
      ) {
        return
      }

      const selectedNode = treeRef.current.getNode(selectedBlockId)
      if (!selectedNode) return

      const access = canAlterBlockSchema({
        b: selectedNode.value,
        parent: selectedNode.parentKey
          ? (treeRef.current.getNode(selectedNode.parentKey)?.value ?? null)
          : null,
      })

      if (access.value === false) {
        if (access.reason) toast.error(access.reason)
        return
      }

      // will delete
      e.preventDefault()

      const selectedEl = document.querySelector(
        `[data-block-id="${idToSourceBlockId(selectedNode.value)}"]`
      )

      if (selectedEl) {
        const captureEl = selectedEl.closest(
          '[data-capture-delete-event="true"]'
        )
        if (captureEl) {
          return captureEl.dispatchEvent(
            new CustomEvent('delete-event', {
              detail: {
                selectedBlockId,
              },
            })
          )
        }
      }

      const selectedBlockSourceBlockId = idToSourceBlockId(selectedNode.value)
      const blockIdToDelete = sourceBlockIdToId(
        selectedBlockSourceBlockId,
        treeRef.current
      )

      const parentChildren = blockTreeManagerRef.current?.getChildrenKeys(
        idToSourceBlockId(block)
      )
      const isLastChild = blockIdToDelete === parentChildren?.at(-1)
      const isOnlyChild = parentChildren?.length === 1

      if (isOnlyChild) {
        setFocus({
          type: 'block:document-title',
          blockId: idToSourceBlockId(block),
        })
      } else if (isLastChild) {
        onFocusPrevListItem(e, { bypassFocusValueCheck: true })
      } else onFocusNextListItem(e, { bypassFocusValueCheck: true })

      const params: DeleteBlockParams = {
        id: blockIdToDelete,
        path: treeRef.current.getPath(selectedNode, {
          required: true,
          format: 'string',
        }),
        idSeed: getId(),
        isoNow: new Date().toISOString(),
      }

      const result = await replicache.mutate
        .deleteBlock(params)
        .catch((err) => {
          if (err instanceof MutatorUserError) {
            toast.error(err.reason)
          } else {
            console.error(err)
            toast.error('An error occurred while deleting the block')
          }
        })

      if (result?.undo) {
        undoManager.current?.push({
          redo: [
            getBlockFocusOp({
              block: {
                id: selectedBlockSourceBlockId,
                sourceBlockId: selectedBlockSourceBlockId,
              },
              focusType: 'block:wrapper',
              preferredTabKey: activeTabKey,
            }),
            { op: 'deleteBlock', args: params },
          ],
          undo: [
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
            getBlockFocusOp({
              block: {
                id: selectedBlockSourceBlockId,
                sourceBlockId: selectedBlockSourceBlockId,
              },
              preferredTabKey: activeTabKey,
              focusType: 'block:wrapper',
            }),
          ],
        })
      }
    },
    [
      block,
      blockTreeManagerRef,
      canAlterBlockSchema,
      activeTabKey,
      onFocusNextListItem,
      onFocusPrevListItem,
      replicache,
      selectedBlockId,
      setFocus,
      treeRef,
      undoManager,
    ]
  )

  const onCopy = React.useCallback(
    async (e: ClipboardEvent) => {
      // first clear the copied blocks from before
      setCutBlocks(null)
      // if the selected block is document itself, we don't want to enable this hotkey
      if (block?.id === selectedBlockId) return

      const { focusIsInsideEditor, focusedElement } = getEditorElement()
      if (!focusIsInsideEditor) return

      const shouldPrevent = getShouldPreventCopyBlock(focusedElement, focusType)
      if (shouldPrevent) return

      // prevent copy of block if user has some text selected
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        if (
          range.collapsed === false ||
          range.startOffset !== range.endOffset
        ) {
          setCutBlocks(null)
          return
        }
      }

      if (!treeRef.current) return
      if (
        (block &&
          e.target instanceof HTMLTextAreaElement &&
          !isCommentBlock(block)) ||
        focusedElement instanceof HTMLTextAreaElement ||
        focusedElement instanceof HTMLInputElement ||
        (focusedElement instanceof HTMLDivElement &&
          focusedElement?.getAttribute('contentEditable') &&
          !focusedElement.parentElement?.dataset.commentBlock)
      ) {
        return
      }

      if (!selectedBlockId) return
      e.preventDefault()

      await copyBlockToClipboard({
        blockId: selectedBlockId,
        blockTreeManager: treeRef.current,
        onSuccess: () => {
          toast('Block copied to clipboard', { icon: <ClipboardCopyIcon /> })
        },
        onError: (err) => {
          console.error(err)
          toast.error('An error occurred while copying block to clipboard')
        },
      })
    },
    [setCutBlocks, block, selectedBlockId, focusType, treeRef]
  )

  const onCut = React.useCallback(
    // selection comes from params and not from window.getSelection() because it's not reliable using tiptap
    async (
      e: ClipboardEvent,
      selection: { start: number; end: number } | null
    ) => {
      if (!selectedBlockId) return

      const { focusIsInsideEditor, focusedElement } = getEditorElement()
      if (!focusIsInsideEditor) return

      const shouldPrevent = getShouldPreventCopyBlock(focusedElement, focusType)
      if (shouldPrevent) return

      // prevent cutting block if user has some text selected
      const domSelection = window.getSelection()
      if (domSelection && domSelection.rangeCount > 0) {
        if (selection && selection.start !== selection.end) {
          setCutBlocks(null)
          return
        }
      }

      if (
        !treeRef.current ||
        !selectedBlockId ||
        (block &&
          focusedElement instanceof HTMLTextAreaElement &&
          !isCommentBlock(block)) ||
        focusedElement instanceof HTMLInputElement ||
        (focusedElement instanceof HTMLDivElement &&
          focusedElement?.getAttribute('contentEditable') &&
          !focusedElement.parentElement?.dataset.commentBlock)
      ) {
        return
      }

      const selectedNode = treeRef.current?.getNode(selectedBlockId)
      if (!selectedNode) return

      const access = canAlterBlockSchema({
        b: selectedNode.value,
        parent: selectedNode.parentKey
          ? (treeRef.current?.getNode(selectedNode.parentKey)?.value ?? null)
          : null,
      })

      if (access.value === false) {
        if (access.reason) toast.error(access.reason)
        return
      }

      e.preventDefault()

      const blockId = selectedBlockId

      await copyBlockToClipboard({
        blockId,
        blockTreeManager: treeRef.current,
        onSuccess: () => {
          toast('Block cut to clipboard', { icon: <ClipboardCopyIcon /> })
          setCutBlocks({
            ids: [blockId],
          })
        },
        onError: (err) => {
          console.error(err)
          toast.error('An error occurred while cutting')
        },
      })
    },
    [
      selectedBlockId,
      focusType,
      treeRef,
      block,
      canAlterBlockSchema,
      setCutBlocks,
    ]
  )

  const onCutBlur = React.useCallback(() => {
    setCutBlocks(null)
  }, [setCutBlocks])

  const onPaste = React.useCallback(
    async (e: ClipboardEvent, shiftKeyPressed: boolean) => {
      const { focusIsInsideEditor, focusedElement } = getEditorElement()
      if (
        !e.clipboardData ||
        !path ||
        !replicache ||
        !block ||
        !focusIsInsideEditor
      ) {
        return
      }

      if (
        !selectedBlockId ||
        focusedElement instanceof HTMLTextAreaElement ||
        focusedElement instanceof HTMLInputElement ||
        (focusedElement instanceof HTMLDivElement &&
          focusedElement?.getAttribute('contentEditable') &&
          !focusedElement.parentElement?.dataset.commentBlock)
      ) {
        return
      }
      const shouldPrevent = getShouldPreventCopyBlock(focusedElement, focusType)

      invariant(treeRef.current, 'blockTreeManager is not defined')
      const selectedNode = treeRef.current.getNode(selectedBlockId)

      if (shouldPrevent) {
        if (selectedNode?.value.type !== 'comment') return
        const content = jsonToPlainText(
          stateToJson(selectedNode.value.value.state),
          commentExtensionsForTransformations
        ).trim()

        if (content) return
      }
      e.preventDefault()

      if (!selectedNode) return

      const pasteResult = await pasteBlocksFromClipboard({
        repoId: data.repoId,
        destinationPath: path,
        selectedBlock: selectedNode.value,
        blockTreeManager: treeRef.current,
        editModeIsEnabled,
        replicache,
        repoStore: { cutBlocks, setCutBlocks },
        addTab,
        shiftKeyPressed,
      })
      if (pasteResult?.undo) {
        const focusOp = {
          op: 'focus',
          args: {
            focus: {
              blockId: idToSourceBlockId(
                pasteResult.newBlock || selectedNode.value
              ),
              type: 'block:wrapper',
            },
            preferredTabKey: activeTabKey,
          },
        } as const
        undoManager.current?.push({
          redo: [pasteResult.redo, focusOp],
          undo: [
            focusOp,
            {
              op: 'blockBatchMutations',
              args: { mutations: pasteResult.undo },
            },
          ],
        })
      }

      setTimeout(() => {
        if (!treeRef.current) return
        if (pasteResult?.newBlock) {
          setFocus({
            blockId: idToSourceBlockId(pasteResult.newBlock),
            type: 'block:wrapper',
          })
        }
      }, 10)
    },
    [
      addTab,
      block,
      cutBlocks,
      editModeIsEnabled,
      activeTabKey,
      focusType,
      path,
      replicache,
      data?.repoId,
      selectedBlockId,
      setCutBlocks,
      setFocus,
      treeRef,
      undoManager,
    ]
  )

  const onRenameBlock = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.isComposing) return
      if (editorMode !== 'write:all') return

      const { focusIsInsideEditor, focusedElement } = getEditorElement()
      if (!focusIsInsideEditor) return

      if (
        selectedBlockId &&
        !['INPUT', 'BUTTON', 'A', 'TEXTAREA'].includes(
          focusedElement?.tagName ?? ''
        ) &&
        !focusedElement?.getAttribute('contentEditable')
      ) {
        if (!!selectedBlockId) {
          e.preventDefault()
          e.stopPropagation()

          setFocus({
            blockId: selectedBlockId,
            type: 'block:name',
          })
          return
        }
      }
    },
    [editorMode, selectedBlockId, setFocus]
  )

  // this ref is a hack to obtain the selection when the user cuts, because in the oncut callback,
  // the selection is returning the selection post cut, and we need the selection pre cut
  const lastSelectionRef = React.useRef<{ start: number; end: number } | null>(
    null
  )

  React.useLayoutEffect(() => {
    if (disabled) return

    let isShiftPressed = false

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.isComposing) return
      if (
        e.target &&
        e.target instanceof HTMLElement &&
        e.target.dataset.ignoreEditorKeydown
      ) {
        return
      }

      if (e.key === 'Shift') isShiftPressed = true
      if (e.key === 'ArrowUp') return onFocusPrevListItem(e)
      if (e.key === 'ArrowDown') return onFocusNextListItem(e)
      if (e.key === 'Escape') {
        onCutBlur()
        return onFocusBlockWrapper(e)
      }
      if (e.key === 'Enter') return onFocusBlockValue(e)
      if (isHotkey('meta+backspace', e)) return onDeleteBlock(e)
      if (isHotkey('r', e)) return onRenameBlock(e)
      // save selection for cut
      if (isHotkey('meta+x', e)) {
        if (!canAlterThisBlocksSchema) return
        const selection = window.getSelection()
        if (!selection || selection.rangeCount <= 0) return
        const range = selection.getRangeAt(0)
        if (!range) return

        lastSelectionRef.current = {
          start: range.startOffset,
          end: range.endOffset,
        }
        return
      }
    }

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') isShiftPressed = false
    }

    const _onCut = (e: ClipboardEvent) => {
      onCut(e, lastSelectionRef.current)
    }

    const _onPaste = (e: ClipboardEvent) => {
      onPaste(e, isShiftPressed)
    }

    document.addEventListener('cut', _onCut)
    document.addEventListener('copy', onCopy)
    document.addEventListener('paste', _onPaste)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
    return () => {
      document.removeEventListener('cut', _onCut)
      document.removeEventListener('copy', onCopy)
      document.removeEventListener('paste', _onPaste)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
    }
  }, [
    canAlterThisBlocksSchema,
    disabled,
    onCopy,
    onCut,
    onCutBlur,
    onDeleteBlock,
    onFocusBlockValue,
    onFocusBlockWrapper,
    onFocusNextListItem,
    onFocusPrevListItem,
    onPaste,
    onRenameBlock,
  ])
}
